import { useState, useEffect, useRef } from 'react';

/**
 * Speech-to-text hook.
 *
 * The recognition runs in *continuous* mode and keeps listening until the
 * user explicitly stops it — it does NOT auto-stop when the speaker pauses.
 * This matters for dictating a whole order: short pauses between items must
 * not cut the recording off mid-sentence. Browsers still fire `onend` on
 * silence, so we transparently restart until the user clicks stop.
 */
export function useVoiceRecognition() {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);
  const finalRef = useRef('');        // accumulated finalized text
  const manualStopRef = useRef(false); // true once the user clicked stop
  const recordingRef = useRef(false);  // mirror of isRecording for event handlers

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setIsSupported(false);
      setError('הדפדפן לא תומך בזיהוי קולי');
      return;
    }

    setIsSupported(true);
    const recognition = new SpeechRecognition();

    // Continuous + interim: keep the session open and surface words live.
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'he-IL';
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;
        if (result.isFinal) {
          finalRef.current += text;
        } else {
          interim += text;
        }
      }
      setTranscript((finalRef.current + interim).trim());
    };

    recognition.onerror = (event) => {
      // 'no-speech' and 'aborted' are expected during long/continuous sessions
      // (silence, or our own restart/cleanup) — don't surface them as errors.
      if (event.error === 'no-speech' || event.error === 'aborted') return;

      let errorMessage = 'שגיאה בזיהוי קולי';
      switch (event.error) {
        case 'not-allowed':
        case 'permission-denied':
          errorMessage = 'נא לאפשר גישה למיקרופון';
          break;
        case 'network':
          errorMessage = 'בעיית רשת, נסה שוב';
          break;
        case 'audio-capture':
          errorMessage = 'המיקרופון לא זמין';
          break;
        default:
          errorMessage = `שגיאה: ${event.error}`;
      }
      setError(errorMessage);
    };

    recognition.onend = () => {
      // The engine stops itself on prolonged silence. If the user hasn't asked
      // to stop, restart so recording continues until they click stop.
      if (!manualStopRef.current && recordingRef.current) {
        try {
          recognition.start();
          return;
        } catch {
          // fall through to "stopped" state if we can't restart
        }
      }
      recordingRef.current = false;
      setIsRecording(false);
    };

    recognitionRef.current = recognition;

    return () => {
      try { recognitionRef.current?.abort(); } catch { /* ignore */ }
    };
  }, []);

  const startRecording = () => {
    if (!isSupported) {
      setError('הדפדפן לא תומך בזיהוי קולי');
      return;
    }
    setError(null);
    finalRef.current = '';
    setTranscript('');
    manualStopRef.current = false;
    try {
      recognitionRef.current?.start();
      recordingRef.current = true;
      setIsRecording(true);
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError('לא ניתן להתחיל הקלטה');
    }
  };

  const stopRecording = () => {
    manualStopRef.current = true;
    recordingRef.current = false;
    try {
      recognitionRef.current?.stop();
    } catch (err) {
      console.error('Failed to stop recording:', err);
    }
    setIsRecording(false);
  };

  const toggleRecording = () => {
    if (recordingRef.current) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return {
    isRecording,
    transcript,
    isSupported,
    error,
    startRecording,
    stopRecording,
    toggleRecording,
  };
}
